import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

interface URLSyncedFiltersConfig<T> {
  defaults: T;
  /** Map URL param names to filter keys (e.g. { entity_type: 'entityType' }) */
  paramKeyMap?: Record<string, keyof T>;
  /** Convert a raw URL string value to the correct type for a given key */
  parseParam?: (key: keyof T, value: string) => any;
  /** Default page size when not specified in URL */
  defaultLimit?: number;
}

interface URLSyncedFiltersResult<T> {
  filters: T & { limit: number; offset: number };
  handleFilterChange: (key: keyof T, value: string) => void;
  handleClearFilters: () => void;
  handlePageChange: (newOffset: number) => void;
  handleLimitChange: (newLimit: number) => void;
  hasActiveFilters: boolean;
}

/**
 * Syncs a filter state object bidirectionally with URL search params.
 * Handles pagination (limit/offset) and resets offset on filter changes.
 */
export function useURLSyncedFilters<T extends Record<string, any>>(
  config: URLSyncedFiltersConfig<T>,
): URLSyncedFiltersResult<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultLimit = config.defaultLimit ?? 50;

  const invertedKeyMap: Record<string, string> = {};
  const forwardKeyMap: Record<string, string> = {};
  if (config.paramKeyMap) {
    for (const [param, filterKey] of Object.entries(config.paramKeyMap)) {
      invertedKeyMap[String(filterKey)] = param;
      forwardKeyMap[param] = String(filterKey);
    }
  }

  const paramNameForKey = (key: string): string => invertedKeyMap[key] ?? key;
  const filterKeyForParam = (param: string): string => forwardKeyMap[param] ?? param;

  const getFiltersFromURL = useCallback((): T & { limit: number; offset: number } => {
    const result: Record<string, any> = {};
    for (const key of Object.keys(config.defaults)) {
      const paramName = paramNameForKey(key);
      const raw = searchParams.get(paramName);
      if (raw) {
        result[key] = config.parseParam ? config.parseParam(key as keyof T, raw) : raw;
      } else {
        result[key] = config.defaults[key];
      }
    }
    result.limit = searchParams.get('limit') ? Number(searchParams.get('limit')) : defaultLimit;
    result.offset = searchParams.get('offset') ? Number(searchParams.get('offset')) : 0;
    return result as T & { limit: number; offset: number };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const updateURL = useCallback((filters: Record<string, any>) => {
    const params = new URLSearchParams();
    for (const key of Object.keys(config.defaults)) {
      const val = filters[key];
      if (val != null && val !== '' && val !== config.defaults[key]) {
        params.set(paramNameForKey(key), String(val));
      }
    }
    if (filters.limit && filters.limit !== defaultLimit) params.set('limit', String(filters.limit));
    if (filters.offset && filters.offset > 0) params.set('offset', String(filters.offset));
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams]);

  const filters = getFiltersFromURL();

  const handleFilterChange = (key: keyof T, value: string) => {
    const current = getFiltersFromURL();
    const updated = { ...current, offset: 0 };
    if (value) {
      (updated as any)[key] = config.parseParam
        ? config.parseParam(key, value)
        : value;
    } else {
      delete (updated as any)[key];
    }
    updateURL(updated);
  };

  const handleClearFilters = () => {
    updateURL({ limit: filters.limit });
  };

  const handlePageChange = (newOffset: number) => {
    const current = getFiltersFromURL();
    updateURL({ ...current, offset: newOffset });
  };

  const handleLimitChange = (newLimit: number) => {
    const current = getFiltersFromURL();
    updateURL({ ...current, limit: newLimit, offset: 0 });
  };

  const hasActiveFilters = Object.keys(config.defaults).some((key) => {
    const val = (filters as any)[key];
    return val != null && val !== '' && val !== config.defaults[key];
  });

  return {
    filters,
    handleFilterChange,
    handleClearFilters,
    handlePageChange,
    handleLimitChange,
    hasActiveFilters,
  };
}
